import { useCallback } from 'react';
import { DropdownMenu, DropdownMenuTrigger } from '@oniratec/onira-react-ui';

import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { useDraggable } from '@/modules/notifications/hooks/useDraggable';
import { NotificationTrigger } from './NotificationTrigger';
import { NotificationsDropdownContent } from './NotificationsDropdownContent';

type Props = {
  compact?: boolean;
};

export const NotificationsBell = ({ compact = false }: Props) => {
  const {
    open,
    loading,
    markingAll,
    unreadCount,
    notifications,
    hasUnread,
    unreadInListCount,
    buttonLabel,
    handleOpenChange,
    handleNotificationClick,
    handleMarkAllAsRead,
  } = useNotifications();

  const onTap = useCallback(() => {
    void handleOpenChange(!open);
  }, [open, handleOpenChange]);

  const {
    containerRef,
    dropdownAlign,
    dropdownSide,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  } = useDraggable({ onTap });

  // Only allow closing from Radix (Escape, click outside), never opening
  const handleOpenChangeGuarded = useCallback(
    async (nextOpen: boolean) => {
      if (!nextOpen) {
        await handleOpenChange(false);
      }
    },
    [handleOpenChange],
  );

  return (
    <div
      ref={containerRef}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      className="fixed z-50"
      style={{ touchAction: 'none' }}
    >
      <DropdownMenu open={open} onOpenChange={handleOpenChangeGuarded}>
        <DropdownMenuTrigger asChild>
          <NotificationTrigger
            compact={compact}
            hasUnread={hasUnread}
            unreadCount={unreadCount}
            buttonLabel={buttonLabel}
          />
        </DropdownMenuTrigger>

        <NotificationsDropdownContent
          notifications={notifications}
          loading={loading}
          markingAll={markingAll}
          unreadInListCount={unreadInListCount}
          dropdownAlign={dropdownAlign}
          dropdownSide={dropdownSide}
          onNotificationClick={n => void handleNotificationClick(n)}
          onMarkAllAsRead={() => void handleMarkAllAsRead()}
        />
      </DropdownMenu>
    </div>
  );
};
